// "Setup Code…" helper. A bottom sheet where the user pastes a setup key /
// secret / otpauth:// URI, or picks a QR-code image to decode. Validates with
// parseOtpInput and hands the raw string back via onPick. Never logs secrets.

import { el } from './dom.js';
import { openSheet } from './sheet.js';
import { parseOtpInput } from '../util/totp.js';
import { decodeQrFromFile } from '../util/qr-decode.js';

// Note: this sheet does NOT manage auto-lock. It only ever opens from the entry
// form, which already pauses auto-lock for its whole lifetime and resumes it on
// its own dismiss. Re-arming the timer here would wrongly fire mid-edit.
export function openTotpSetup({ current, onPick } = {}) {
  const input = el('input', {
    type: 'text',
    placeholder: 'Paste setup key, secret, or otpauth:// URI',
    value: current?.totp ?? '',
    // This field holds a raw TOTP seed in cleartext — keep it away from
    // browser/extension spellcheck, which uploads type=text contents.
    spellcheck: false,
    autocorrect: 'off',
    autocapitalize: 'off',
    autocomplete: 'off',
    'data-1p-ignore': '',
    oninput: () => validate(),
  });

  const hint = el('div', { class: 'totp-setup-hint' });

  const fileInput = el('input', {
    type: 'file',
    accept: 'image/*',
    capture: 'environment',
    style: { display: 'none' },
    onchange: async (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = ''; // allow re-picking the same file later
      if (!file) return;
      hint.textContent = 'Reading image…';
      const decoded = await decodeQrFromFile(file);
      if (decoded) {
        input.value = decoded;
        validate();
      } else {
        hint.textContent = "Couldn't read a QR code in that image.";
      }
    },
  });

  const scanBtn = el('button', {
    class: 'ghost', type: 'button', onclick: () => fileInput.click(),
  }, ['Scan QR image']);

  const saveBtn = el('button', {
    class: 'primary', type: 'button', onclick: () => {
      const raw = input.value.trim();
      if (!parseOtpInput(raw)) { validate(); return; }
      onPick?.(raw);
      handle.close();
    },
  }, ['Save']);

  const cancelBtn = el('button', {
    class: 'ghost', type: 'button', onclick: () => handle.close(),
  }, ['Cancel']);

  function validate() {
    const raw = input.value.trim();
    if (raw.length === 0) {
      hint.textContent = '';
      saveBtn.disabled = true;
      return;
    }
    if (parseOtpInput(raw)) {
      hint.textContent = '✓ Looks valid';
      saveBtn.disabled = false;
    } else {
      hint.textContent = 'Not a valid setup key or otpauth:// URI.';
      saveBtn.disabled = true;
    }
  }

  const body = el('div', { class: 'totp-setup' }, [
    el('label', { class: 'field full' }, [
      el('span', {}, ['One-time password']),
      input,
    ]),
    scanBtn,
    fileInput,
    hint,
    el('div', { class: 'form-actions' }, [cancelBtn, saveBtn]),
  ]);

  const handle = openSheet({
    kind: 'bottom',
    title: 'Setup code',
    body,
  });

  validate();
}
